import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Award, Download, Calendar, Search, Send } from "lucide-react";
import { fmtDate, daysUntil } from "@/lib/helpers";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Certificates() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [q, setQ] = useState("");

  return (
    <div>
      <PageHeader title={isAdmin ? "Issued Certificates" : "My Certificate Wallet"} subtitle={isAdmin ? "All certificates issued to staff and course candidates." : "Your earned certifications — view, verify and download."} testid="certificates-header" />
      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by course, name, email or code..." className="pl-9" data-testid="search-certificates-input" />
      </div>
      {isAdmin ? (
        <Tabs defaultValue="candidates">
          <TabsList className="mb-5">
            <TabsTrigger value="candidates" data-testid="certificates-tab-candidates">Candidates</TabsTrigger>
            <TabsTrigger value="staff" data-testid="certificates-tab-staff">Staff</TabsTrigger>
          </TabsList>
          <TabsContent value="candidates"><CandidateCertificates q={q} /></TabsContent>
          <TabsContent value="staff"><StaffCertificates q={q} isAdmin /></TabsContent>
        </Tabs>
      ) : <StaffCertificates q={q} isAdmin={false} />}
    </div>
  );
}

function CandidateCertificates({ q }) {
  const [rows, setRows] = useState(null);
  const [sending, setSending] = useState(null);
  useEffect(() => { api.get("/candidate-certificates").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <Loading />;
  const s = q.trim().toLowerCase();
  const visible = rows.filter((r) => !s || [r.candidate_name, r.candidate_email, r.course_title, r.certificate?.code, String(r.certificate?.number || "")]
    .some((v) => (v || "").toLowerCase().includes(s)));

  const resend = async (r) => {
    setSending(r.session_id);
    try {
      await api.post(`/candidate-certificates/${r.session_id}/resend`);
      toast.success(`Certificate re-sent to ${r.candidate_email}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSending(null);
  };

  if (visible.length === 0) return <EmptyState message="No candidate certificates yet. They're issued when a candidate is marked present (E-learning courses excluded)." icon={Award} />;
  return (
    <Card className="overflow-hidden" data-testid="candidate-certificates-table">
      <Table>
        <TableHeader><TableRow><TableHead>Candidate</TableHead><TableHead>Course</TableHead><TableHead>Certificate ID</TableHead><TableHead>Issued</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {visible.map((r) => (
            <TableRow key={r.session_id} data-testid={`candidate-cert-row-${r.session_id}`}>
              <TableCell><p className="font-medium">{r.candidate_name}</p><p className="text-xs text-muted-foreground">{r.candidate_email}</p></TableCell>
              <TableCell className="max-w-xs">{r.course_title}</TableCell>
              <TableCell className="font-mono text-xs" data-testid={`candidate-cert-number-${r.session_id}`}>{r.certificate?.number && <span className="block font-semibold text-sm text-foreground">{r.certificate.number}</span>}{r.certificate?.code}</TableCell>
              <TableCell className="text-sm">{fmtDate(r.certificate?.issue_date)}{r.attendance !== "present" && <span className="block text-xs text-red-600">Attendance changed</span>}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {r.attendance === "present" && (
                  <>
                    <a href={`${BACKEND}/api/access/${r.access_token}/certificate`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" data-testid={`candidate-cert-download-${r.session_id}`}><Download className="h-4 w-4 mr-1" />View</Button>
                    </a>
                    <Button variant="outline" size="sm" onClick={() => resend(r)} disabled={sending === r.session_id} data-testid={`candidate-cert-resend-${r.session_id}`}><Send className="h-4 w-4 mr-1" />{sending === r.session_id ? "Sending..." : "Resend"}</Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function StaffCertificates({ q, isAdmin }) {
  const [certs, setCerts] = useState(null);
  const [view, setView] = useState(null);
  useEffect(() => { api.get(isAdmin ? "/certificates" : "/certificates/me").then(({ data }) => setCerts(data)); }, [isAdmin]);
  if (!certs) return <Loading />;
  const s = q.trim().toLowerCase();
  const filtered = certs.filter((c) => !s || [c.course_title, c.user_name, c.cert_code, String(c.cert_number || "")].some((v) => (v || "").toLowerCase().includes(s)));
  if (filtered.length === 0) return <EmptyState message={isAdmin ? "No staff certificates have been issued yet." : "Complete a course or pass an assessment to earn your first certificate."} icon={Award} />;
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((c) => <StaffCertCard key={c.id} c={c} isAdmin={isAdmin} onOpen={() => setView(c)} />)}
      </div>
      {view && <CertificateModal cert={view} onClose={() => setView(null)} />}
    </>
  );
}

function StaffCertCard({ c, isAdmin, onOpen }) {
  const left = daysUntil(c.expiry_date);
  const expired = left != null && left < 0;
  const expiring = left != null && left >= 0 && left < 90;
  return (
    <Card className="overflow-hidden stagger-in border-border cursor-pointer group" onClick={onOpen} data-testid={`certificate-card-${c.id}`}>
      <div className="h-2 bg-gradient-to-r from-primary to-emerald-500" />
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center"><Award className="h-6 w-6 text-primary" /></div>
          {expired ? <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400">Expired</span>
            : expiring ? <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">{left}d left</span>
            : <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">Valid</span>}
        </div>
        <h3 className="font-heading font-semibold mt-4 leading-snug line-clamp-2 min-h-[2.75rem]">{c.course_title}</h3>
        {isAdmin && <p className="text-sm text-muted-foreground mt-1">{c.user_name}</p>}
        <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
          <span className="font-mono">{c.cert_number ? `ID ${c.cert_number} · ` : ""}{c.cert_code}</span>
          {c.score != null && <span className="font-medium text-foreground">{c.score}%</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
          <Calendar className="h-3.5 w-3.5" />Issued {fmtDate(c.issue_date)}{c.expiry_date && ` · Expires ${fmtDate(c.expiry_date)}`}
        </div>
      </div>
    </Card>
  );
}

function CertificateModal({ cert, onClose }) {
  const url = `${BACKEND}/api/certificates/${cert.id}/pdf`;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden bg-white">
        <iframe src={`${url}#toolbar=0&view=FitH`} title="Certificate" className="w-full border-0" style={{ height: "70vh" }} data-testid="certificate-preview" />
        <div className="p-4 flex justify-end gap-2 border-t border-border bg-background">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Button data-testid="certificate-download-pdf-button"><Download className="h-4 w-4 mr-1.5" />Download / Print</Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
